import { useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import type { PlatformFundingBankDetails } from "components/platform/rewards/types";

export type ConfirmPlatformBank = (
  clientSecret: string,
  details: PlatformFundingBankDetails,
  publishableKey?: string | null,
) => Promise<string>;

// Confirms a funding bank against the setup the server began on the pool's Stripe account, under that account's key
export function useConfirmPlatformBank(): ConfirmPlatformBank {
  const { t } = useTranslation();
  return useCallback(
    async (clientSecret, details, publishableKey) => {
      const stripe = await loadStripe(
        publishableKey ||
          (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string),
      );
      if (!stripe)
        throw new Error(t("SomethingWentWrongDescriptionWithSupport"));
      const confirmed = await stripe.confirmUsBankAccountSetup(clientSecret, {
        payment_method: {
          billing_details: {
            name: details.accountHolderName,
            email: details.email,
          },
          us_bank_account: {
            account_number: details.accountNumber,
            routing_number: details.routingNumber,
            account_holder_type: "company",
            account_type: details.accountType,
          },
        },
      });
      if (confirmed.error) throw new Error(confirmed.error.message);
      if (!confirmed.setupIntent?.id) {
        throw new Error(t("SomethingWentWrongDescriptionWithSupport"));
      }
      return confirmed.setupIntent.id;
    },
    [t],
  );
}

export default useConfirmPlatformBank;
